import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import ReactSelectOptionModel from "../../../commons/models/ReactSelectOptionModel";
import { ShowSnackbarModel } from "../../../commons/scaffold/scaffold";
import SubmitVendorItemModel from "../models/submit_vendor_item_model";
import VendorItemModel from "../models/vendor_item_model";
import VendorModel from "../models/vendor_model";

export default class VendorItemDashboardViewModel extends ProviderVM {
    snackbarModel?: ShowSnackbarModel;

    // Vendor list
    isLoadingVendorList: boolean = false;
    vendorList: VendorModel[] = [];
    filteredVendorList: VendorModel[] = [];
    searchKeyword: string = "";

    // Vendor detail
    isLoadingVendorDetail: boolean = false;
    isLoadingVendorItemOptions: boolean = false;
    isShowingVendorDetail: boolean = false;
    selectedVendorId?: number;
    selectedVendorName = "";
    currentVendorItems: VendorItemModel[] = [];
    searchCurrentVendorItemsKeyword = "";
    filteredCurrentVendorItems: VendorItemModel[] = [];
    currentVendorItemOptions: VendorItemModel[] = [];
    addedItemIds: number[] = [];
    selectedItemId?: number;
    isSubmittingVendorItems: boolean = false;

    // Create vendor
    isShowingCreateVendorModal: boolean = false;
    newVendorName: string = "";
    isSubmittingNewVendor: boolean = false;

    constructor() {
        super();
        this.getVendorList();
    }

    // Vendor list
    filterVendorList(keyword: string) {
        this.emit(() => {
            this.searchKeyword = keyword;
            this.filteredVendorList = this.vendorList.filter((vendor) =>
                vendor.name.toLowerCase().includes(keyword.toLowerCase())
            );
        });
    }

    getVendorList() {
        this.emit(() => {
            this.isLoadingVendorList = true;
        });

        ApiService.fetch(Endpoints.getVendorList, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<VendorModel[]>) => {
                if (response.code === 200) {
                    const vendorList = response.data as VendorModel[];
                    this.emit(() => {
                        this.isLoadingVendorList = false;
                        this.vendorList = vendorList;
                        this.filteredVendorList = vendorList;
                    });
                } else {
                    this.emit(() => {
                        this.isLoadingVendorList = false;
                        this.snackbarModel = {
                            message:
                                "Gagal mendapatkan daftar vendor, silakan coba refresh",
                            key: Date.now().toString(),
                        };
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.emit(() => {
                    this.isLoadingVendorList = false;
                    this.snackbarModel = {
                        key: Date.now().toString(),
                        message:
                            "Gagal mendapatkan daftar vendor, silakan coba refresh",
                    };
                });
            },
        });
    }

    // Vendor detail (items)
    getVendorDetail(vendorId: number) {
        this.emit(() => {
            this.isLoadingVendorDetail = true;
        });

        ApiService.fetchWithQueryParams(Endpoints.getVendorCurrentItems, {
            httpMethod: HttpMethod.get,
            queryParameters: {
                vendorId: vendorId,
            },
            onSuccess: (response: BaseResponse<VendorItemModel[]>) => {
                const currentVendorItems = response.data as VendorItemModel[];

                this.emit(() => {
                    this.isLoadingVendorDetail = false;
                    this.isShowingVendorDetail = true;
                    this.selectedVendorId = vendorId;
                    this.selectedVendorName =
                        this.vendorList.find((vendor) => vendor.id === vendorId)
                            ?.name ?? "";
                    this.currentVendorItems = currentVendorItems;
                    this.filteredCurrentVendorItems = currentVendorItems;
                });
            },
            onFailure: (error) => {
                console.log(error);
                this.emit(() => {
                    this.isLoadingVendorDetail = false;
                    this.snackbarModel = {
                        key: Date.now().toString(),
                        message:
                            "Gagal mendapatkan item vendor, silakan coba refresh halaman",
                    };
                });
            },
        });

        this.emit(() => {
            this.isLoadingVendorItemOptions = true;
        });

        ApiService.fetchWithQueryParams(Endpoints.getVendorItemOptions, {
            httpMethod: HttpMethod.get,
            queryParameters: {
                vendorId: vendorId,
            },
            onSuccess: (response: BaseResponse<VendorItemModel[]>) => {
                this.emit(() => {
                    this.isLoadingVendorItemOptions = false;
                    this.currentVendorItemOptions =
                        response.data as VendorItemModel[];
                });
            },
            onFailure: (error) => {
                console.log(error);
                this.emit(() => {
                    this.isLoadingVendorItemOptions = false;
                    this.snackbarModel = {
                        message:
                            "Gagal mendapatkan pilihan item vendor, silakan coba refresh halaman",
                        key: Date.now().toString(),
                    };
                });
            },
        });
    }

    changeSelectedItem(id: number) {
        this.emit(() => {
            this.selectedItemId = id;
        });
    }

    handleAddItem(id: number) {
        if (this.addedItemIds.includes(id)) return;

        const updatedAddedItemIds = [...this.addedItemIds, id];
        this.emit(() => {
            this.addedItemIds = updatedAddedItemIds;
        });

        const addedItem = this.currentVendorItemOptions.find(
            (item) => item.id === id
        );

        if (addedItem) {
            const updatedCurrentVendorItems: VendorItemModel[] = [
                ...this.currentVendorItems,
                addedItem,
            ];

            this.emit(() => {
                this.currentVendorItems = updatedCurrentVendorItems;
                this.filteredCurrentVendorItems = updatedCurrentVendorItems;
                this.searchCurrentVendorItemsKeyword = "";
            });
        }
    }

    changeSearchCurrentVendorItemsKeyword(value: string) {
        this.emit(() => {
            this.searchCurrentVendorItemsKeyword = value;
            this.filteredCurrentVendorItems = this.currentVendorItems.filter(
                (item) => item.name.toLowerCase().includes(value.toLowerCase())
            );
        });
    }

    submitVendorItems() {
        if (!this.selectedVendorId || this.addedItemIds.length === 0) {
            this.emit(() => {
                this.snackbarModel = {
                    message:
                        "Vendor belum dipilih atau item yang ditambahkan kosong",
                    key: Date.now().toString(),
                };
            });
            return;
        }

        this.emit(() => {
            this.isSubmittingVendorItems = true;
        });

        const parameters: SubmitVendorItemModel = {
            vendorId: this.selectedVendorId,
            itemIds: this.addedItemIds,
        };

        ApiService.fetch(Endpoints.submitVendorItems, {
            httpMethod: HttpMethod.post,
            parameters: parameters,
            onSuccess: (response: BaseResponse<{}>) => {
                this.emit(() => {
                    this.isSubmittingVendorItems = false;
                    this.snackbarModel = {
                        key: Date.now().toString(),
                        message: response.message,
                        type: "normal",
                    };
                });

                this.closeVendorDetail();
            },
            onFailure: (error) => {
                console.log(error);
                this.emit(() => {
                    this.isSubmittingVendorItems = false;
                    this.snackbarModel = {
                        message:
                            "Gagal submit tambahan item vendor, silakan coba refresh halaman ini",
                        key: Date.now().toString(),
                    };
                });
            },
        });
    }

    getVendorItemOptions(): ReactSelectOptionModel[] {
        return this.currentVendorItemOptions.map((item) => ({
            label: item.name,
            value: item.id,
        }));
    }

    closeVendorDetail() {
        this.emit(() => {
            this.selectedVendorName = "";
            this.selectedVendorId = undefined;
            this.isShowingVendorDetail = false;
            this.currentVendorItems = [];
            this.currentVendorItemOptions = [];
            this.addedItemIds = [];
            this.selectedItemId = undefined;
        });
    }

    // Create new vendor
    openCreateVendorModal() {
        this.emit(() => {
            this.isShowingCreateVendorModal = true;
        });
    }

    closeCreateVendorModal() {
        this.emit(() => {
            this.isShowingCreateVendorModal = false;
        });
    }

    changeVendorName(name: string) {
        this.emit(() => {
            this.newVendorName = name;
        });
    }

    submitNewVendor() {
        if (this.newVendorName.length < 1) {
            this.snackbarModel = {
                key: Date.now().toString(),
                message: "Nama vendor tidak boleh kosong",
                type: "error",
            };
            return;
        }

        this.emit(() => {
            this.isSubmittingNewVendor = true;
        });

        ApiService.fetch(Endpoints.submitNewVendor, {
            httpMethod: HttpMethod.post,
            parameters: {
                name: this.newVendorName,
            },
            onSuccess: (response: BaseResponse<{}>) => {
                this.emit(() => {
                    this.isSubmittingNewVendor = false;
                    this.newVendorName =
                        response.code === 200 ? "" : this.newVendorName;
                    this.snackbarModel = {
                        key: Date.now().toString(),
                        message: response.message,
                        type: response.code === 200 ? "normal" : "error",
                    };
                    this.isShowingCreateVendorModal = response.code !== 200;
                });

                if (response.code === 200) {
                    this.getVendorList();
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.emit(() => {
                    this.isSubmittingNewVendor = false;
                    this.snackbarModel = {
                        key: Date.now().toString(),
                        message:
                            "Gagal memasukkan vendor baru, silakan coba refresh halaman ini",
                        type: "error",
                    };
                });
            },
        });
    }
}
